/**
 * LegalProse — shared building blocks for the Terms of Service and Privacy
 * Notice bodies.
 *
 * Both documents are long, numbered, and rendered in three places (the login
 * footer dialogs, the register / DV-claim links, and the blocking acceptance
 * gate), so their headings, tables, and link styling live here rather than
 * being duplicated per document.
 */

import type { ReactNode } from 'react';

/** Masthead: publisher, version, effective date, and what it supersedes. */
export function LegalDocumentHeader({
  title,
  version,
  effectiveDate,
  supersedes,
  children,
}: {
  title: string;
  version: string;
  effectiveDate: string;
  supersedes: string;
  children?: ReactNode;
}) {
  return (
    <header className="space-y-2 border-b border-border pb-5">
      <h1 className="text-lg font-bold text-foreground">{title}</h1>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Stevenson Systems, Inc. · {version} · Effective {effectiveDate}
      </p>
      <p className="text-xs text-muted-foreground">{supersedes}</p>
      {children}
    </header>
  );
}

/**
 * A numbered top-level section. The `id` is the scroll target for the table of
 * contents, so it must stay in sync with the `LegalTableOfContents` entry.
 */
export function LegalSection({
  id,
  heading,
  children,
}: {
  id: string;
  heading: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 id={id} className="scroll-mt-4 text-base font-bold text-foreground">
        {heading}
      </h2>
      {children}
    </section>
  );
}

export function LegalSubHeading({ children }: { children: string }) {
  return <h3 className="text-sm font-bold text-foreground">{children}</h3>;
}

/** Plain-language summary that opens a section in the source documents. */
export function LegalInShort({ children }: { children: ReactNode }) {
  return (
    <p className="text-sm italic text-muted-foreground">
      <span className="font-semibold not-italic text-foreground">In short:</span>{' '}
      {children}
    </p>
  );
}

export function LegalList({ children }: { children: ReactNode }) {
  return <ul className="list-disc space-y-2 pl-5">{children}</ul>;
}

export function LegalOrderedList({ children }: { children: ReactNode }) {
  return <ol className="list-decimal space-y-2 pl-5">{children}</ol>;
}

/**
 * Horizontally scrollable data table. The documents contain two — the CCPA
 * category map and the sub-processor list — and both have to stay readable in
 * the dialog's narrow column without truncating cells.
 *
 * Cells are plain strings so the first column (a unique category or provider
 * name in both tables) can serve as the row key.
 *
 * The accessible name is an `aria-label` rather than a `<caption>` on purpose:
 * a caption would inject words that are not in the published legal text, and
 * these documents are reproduced verbatim.
 */
export function LegalTable({
  label,
  headers,
  rows,
}: {
  label: string;
  headers: string[];
  rows: string[][];
}) {
  return (
    <div className="overflow-x-auto">
      <table
        aria-label={label}
        className="w-full min-w-xl border-collapse text-left text-sm"
      >
        <thead>
          <tr className="border-b border-border">
            {headers.map((header) => (
              <th
                key={header}
                scope="col"
                className="py-2 pr-4 align-bottom font-semibold text-foreground"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells) => (
            <tr key={cells[0]} className="border-b border-border/60 align-top">
              {cells.map((cell, cellIndex) => (
                <td key={headers[cellIndex] ?? cell} className="py-2 pr-4">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LegalExternalLink({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-2 hover:text-primary"
    >
      {children}
    </a>
  );
}

export function LegalMailto({ email }: { email: string }) {
  return (
    <a
      href={`mailto:${email}`}
      className="text-primary underline underline-offset-2 hover:text-primary"
    >
      {email}
    </a>
  );
}
